import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Filter, Search, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useAuth } from '@/hooks/use-auth';
import { useLocation } from '@/contexts/LocationContext';
import { getActivityLogs, getActionDisplayName, getEntityDisplayName, type ActivityAction, type ActivityEntity } from '@/lib/activity-log';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDistanceToNow } from 'date-fns';
import { tr as trLocale } from 'date-fns/locale';

export default function ActivityLog() {
  const { companyId, user } = useAuth();
  const { currentLocation } = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<ActivityEntity | 'all'>('all');
  const [selectedAction, setSelectedAction] = useState<ActivityAction | 'all'>('all');
  const [selectedUserId, setSelectedUserId] = useState<string | 'all'>('all');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'activity-logs',
      companyId,
      selectedEntity,
      selectedAction,
      selectedUserId,
      currentLocation?.id,
      startDate?.toISOString(),
      endDate?.toISOString(),
      page,
    ],
    queryFn: async () => {
      if (!companyId) return { data: [], count: 0 };

      return await getActivityLogs({
        companyId,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        entity: selectedEntity !== 'all' ? selectedEntity : undefined,
        action: selectedAction !== 'all' ? selectedAction : undefined,
        userId: selectedUserId !== 'all' ? selectedUserId : undefined,
        locationId: currentLocation?.id,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      });
    },
    enabled: !!companyId,
  });

  const filteredData = data?.data.filter(log => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      getEntityDisplayName(log.entity as ActivityEntity).toLowerCase().includes(searchLower) ||
      getActionDisplayName(log.action as ActivityAction).toLowerCase().includes(searchLower) ||
      log.actor_user?.full_name?.toLowerCase().includes(searchLower) ||
      log.actor_user?.email?.toLowerCase().includes(searchLower) ||
      log.detail?.name?.toLowerCase().includes(searchLower) ||
      log.detail?.title?.toLowerCase().includes(searchLower)
    );
  }) || [];

  const totalPages = data?.count ? Math.ceil(data.count / pageSize) : 1;

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedEntity('all');
    setSelectedAction('all');
    setSelectedUserId('all');
    setStartDate(undefined);
    setEndDate(undefined);
    setPage(1);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Aktivite Logları</h1>
            <p className="text-muted-foreground">
              Tüm sistem aktivitelerini görüntüleyin ve filtreleyin
            </p>
          </div>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Yenile
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtreler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Ara</label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Varlık</label>
                <Select value={selectedEntity} onValueChange={(value) => {
                  setSelectedEntity(value as ActivityEntity | 'all');
                  setPage(1);
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="product">Ürün</SelectItem>
                    <SelectItem value="customer">Müşteri</SelectItem>
                    <SelectItem value="sale">Satış</SelectItem>
                    <SelectItem value="visit">Ziyaret</SelectItem>
                    <SelectItem value="package">Paket</SelectItem>
                    <SelectItem value="user">Kullanıcı</SelectItem>
                    <SelectItem value="location">Lokasyon</SelectItem>
                    <SelectItem value="trade">Bayi İşlemi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Aksiyon</label>
                <Select value={selectedAction} onValueChange={(value) => {
                  setSelectedAction(value as ActivityAction | 'all');
                  setPage(1);
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="create">Oluşturuldu</SelectItem>
                    <SelectItem value="update">Güncellendi</SelectItem>
                    <SelectItem value="delete">Silindi</SelectItem>
                    <SelectItem value="view">Görüntülendi</SelectItem>
                    <SelectItem value="export">Dışa Aktarıldı</SelectItem>
                    <SelectItem value="print">Yazdırıldı</SelectItem>
                    <SelectItem value="payment">Ödeme Yapıldı</SelectItem>
                    <SelectItem value="adjustment">Düzenleme</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Başlangıç Tarihi</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP", { locale: tr }) : "Tarih seç"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Bitiş Tarihi</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "PPP", { locale: tr }) : "Tarih seç"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <Button onClick={clearFilters} variant="outline" size="sm">
                Filtreleri Temizle
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Activity Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle>
              Aktivite Logları ({data?.count || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Yükleniyor...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-destructive">Hata: {error instanceof Error ? error.message : 'Bilinmeyen hata'}</p>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Aktivite logu bulunamadı</p>
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih/Saat</TableHead>
                        <TableHead>Kullanıcı</TableHead>
                        <TableHead>Aksiyon</TableHead>
                        <TableHead>Varlık</TableHead>
                        <TableHead>Detay</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="whitespace-nowrap">
                            <div className="text-sm">
                              {format(new Date(log.ts), 'dd.MM.yyyy HH:mm', { locale: tr })}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(log.ts), { 
                                addSuffix: true, 
                                locale: trLocale 
                              })}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm font-medium">
                              {log.actor_user?.full_name || log.actor_user?.email || 'Bilinmeyen'}
                            </div>
                            {log.actor_user?.email && (
                              <div className="text-xs text-muted-foreground">
                                {log.actor_user.email}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {getActionDisplayName(log.action as ActivityAction)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {getEntityDisplayName(log.entity as ActivityEntity)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {log.detail?.name && (
                                <div>İsim: {log.detail.name}</div>
                              )}
                              {log.detail?.title && (
                                <div>Başlık: {log.detail.title}</div>
                              )}
                              {log.detail?.quantity !== undefined && (
                                <div>Miktar: {log.detail.quantity}</div>
                              )}
                              {log.detail?.amount !== undefined && (
                                <div>Tutar: ₺{log.detail.amount.toFixed(2)}</div>
                              )}
                              {log.detail?.changes && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Değişiklikler: {JSON.stringify(log.detail.changes)}
                                </div>
                              )}
                              {log.entity_id && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  ID: {log.entity_id.substring(0, 8)}...
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-muted-foreground">
                      Sayfa {page} / {totalPages}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        Önceki
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        Sonraki
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

